import {
  type AuftraggeberBuergschaftVerwaltenWorkflowSchema,
  type AuftraggeberInanspruchnahmeWorkflowSchema,
  type AvallinieVerwaltenWorkflowSchema,
  type TeilenthaftungEintragenWorkflowSchema,
  type VertragBearbeitenWorkflowSchema,
  type VertragAnlegenWorkflowSchema,
  type VertragDetailSiteSchema,
  type VertragZuordnenWorkflowSchema,
  type AvallinieHinzufuegenWorkflowSchema,
  type AvallinieBearbeitenWorkflowSchema,
  type AuftraggeberBuergschaftHinzufuegenWorkflowSchema,
  type AuftraggeberBuergschaftBearbeitenWorkflowSchema,
  type TextvorlageHinzufuegenWorkflowSchema,
} from '@/lang/types/types.ts';
import type { StammdatenVerwaltenAuftraggeberWorkflowSchema } from '@/lang/types/auftraggeber/unternehmen.ts';

export const stammdatenGesellschaftDatenAuftraggeberFormular: StammdatenVerwaltenAuftraggeberWorkflowSchema =
  {
    banner: {
      eingaben: {
        header: 'Bitte überprüfen Sie alle Eingaben vor dem Speichern',
        text: 'Beachten Sie, dass die Unternehmensdaten nach dem Speichern nur von Trustlog-Mitarbeiter:innen bearbeitet werden können.',
      },
      beachten: {
        header: 'Bitte beachten Sie',
        text:
          'Beachten Sie, dass die Unternehmensdaten nach dem Speichern nur von Trustlog-Mitarbeiter:innen bearbeitet werden können.\n' +
          'Sollten Sie eine eingeschränkte Sichtberechtigung auf die Bürgschaften verwenden, ordnen Sie bitte Ihre Mitarbeiter:innen bei Bedarf dem Unternehmen zu.',
      },
    },
    header: {
      titel: {
        hinzufuegen: 'Unternehmen hinzufügen',
        daten: 'Details zum Unternehmen',
      },
    },
    sections: {
      header: {
        titel: 'Angaben zum Unternehmen',
        hinweisPruefen: 'Bitte prüfen Sie die Angaben des Unternehmens.',
      },
      firmierung: {
        label: 'Firmierung',
        nameHandelsregister: {
          label: 'Name laut Handelsregister',
          validationName: 'name',
        },
      },
      anschrift: {
        label: 'Anschrift',
      },
      weitereAngaben: {
        label: 'Weitere Angaben',
        gesellschaftsform: {
          label: 'Gesellschaftsform',
          validationName: 'gesellschaftsform',
        },
        handelsregisternummer: {
          label: 'Handelsregisternummer',
          validationName: 'handelsregisternummer',
        },
        handelsregister: {
          label: 'Handelsregister',
          validationName: 'handelsregister',
        },
      },
    },
  };

export const textvorlageHinzufuegenWorkflow: TextvorlageHinzufuegenWorkflowSchema = {
  contentHeader: 'Textvorlage hinzufügen',
  banner: {
    info: {
      headline: 'Offene Aufgabe',
      text: 'Zu dieser Bürgschaftsart ist noch eine offene Aufgabe zu erledigen. Wollen Sie diese zuerst bearbeiten?',
    },
    error: {
      headline: 'Text bereits vorhanden',
      text: 'Zu den gewählten Eigenschaften existiert bereits ein Text. Sie können ein anderes Gültigkeitsdatum wählen oder den Vorgang abbrechen.',
    },
  },
  steps: {
    textart: {
      title: 'Textart auswählen',
      beschreibung:
        'Bitte wählen Sie, ob Sie einen für Ihr Unternehmen erstellten Auftraggebertext, einen öffentlichen Text oder die Standardtexte der teilnehmenden Bürgen nutzen möchten.',
      error: 'Bitte wählen Sie eine Textart aus',
      gruppen: {
        buergschaftsart: 'Bürgschaftsart',
        textart: 'Textart',
      },
      buergschaftsart: 'Bürgschaftsart',
      radioOptionen: {
        eigene: {
          label: 'Eigene Vorlage hinzufügen',
          subheading:
            'Laden Sie eine eigene Vorlage hoch. Der Inhalt wird anschließend von den teilnehmenden Bürgen geprüft.',
        },
        oeffentlich: {
          label: 'Öffentlichen Text verwenden',
          subheading:
            'Verwenden Sie einen öffentlichen Mustertext, um diesen umgehend Ihren Auftragnehmern zur Verfügung zu stellen.',
        },
        buergen: {
          label: 'Bürgentext verwenden',
          subheading:
            'Wählen Sie die Mustertexte der Bürgen, um diese umgehend Ihren Auftragnehmern zur Verfügung zu stellen.',
        },
      },
    },
    konfigurieren: {
      titleBuergen: 'Bürgentext konfigurieren',
      titleEigen: 'Eigene Vorlage konfigurieren',
      titleOeffentlich: 'Öffentlichen Text konfigurieren',
      beschreibung:
        'Wählen Sie die Eigenschaften für Ihre Bürgschaften aus, die Ihren Auftragnehmern bei der Beauftragung von Bürgschaften vorgeblendet werden sollen.',
      beschreibungEigen:
        'Bitte laden Sie Ihre eigene Textvorlage als Datei hoch. Diese wird einmalig von den Bürgen geprüft. Der Text wird nach Freigabe Ihren Auftragnehmern bei der Beauftragung einer Bürgschaft automatisch zur Verfügung gestellt.',
      gruppen: {
        baurecht: 'Baurecht',
        befristung: 'Befristung',
        geltungsbeginn: 'Geltungsbeginn',
        textart: 'Textart',
        textvorlage: 'Textvorlage',
        interneTextbezeichnung: 'Interne Textbezeichnung',
        gueltigFuerBuergen: 'Bürgen',
      },
      interneTextbezeichnung: {
        field: 'Bezeichnung',
        error: 'Bitte geben Sie mindestens 5 Zeichen ein',
      },
      textart: {
        field: 'Art des öffentlichen Textes',
      },
      geltungsbeginn: {
        field: 'Geltungsbeginn',
        inline:
          'Das Datum bezieht sich auf das Vertragsdatum Ihrer Bau- oder Werkverträge. Bei der erstmaligen Textanlage sollte das Datum möglichst weit in der Vergangenheit liegen, um auch für weiter zurückliegende Vorhaben digitale Bürgschaften zu erhalten.',
      },
      gueltigFuerBuergen: {
        inline: 'Die Textvorlage wird den unterstehenden Bürgen zur Prüfung vorgelegt.',
      },
      statusDate: 'Stand {statusDate}',
    },
    pruefen: {
      title: 'Eingaben prüfen',
      beschreibungEigen:
        'Bitte überprüfen Sie Ihre angegebenen Daten, bevor Sie den Text absenden und geben Sie optional einen Ansprechpartner für mögliche Rückfragen der Bürgen an.',
      beschreibungBuergen:
        'Bitte überprüfen Sie Ihre angegebenen Daten, bevor Sie den Bürgentext aktivieren.',
      beschreibungOeffentlich:
        'Bitte überprüfen Sie Ihre angegebenen Daten, bevor Sie den öffentlichen Text aktivieren.',
      gruppen: {
        texteigenschaften: 'Texteigenschaften',
        textvorlage: 'Textvorlage',
        ansprechpartner: 'Ansprechpartner',
      },
      header: {
        befristung: 'Befristung',
        buergschaftsart: 'Bürgschaftsart',
        baurecht: 'Baurecht',
        bezeichnung: 'Bezeichnung',
        geltungsbeginn: 'Geltungsbeginn',
        buergen: 'Bürgen',
        status: 'Status',
        textart: 'Art des öff. Textes',
      },
      ansprechpartner: {
        vorname: 'Vorname',
        nachname: 'Nachname',
        telefonnummer: 'Telefonnummer',
        email: 'E-Mail-Adresse',
        meineKontaktdatenEintragen: 'Meine Kontaktdaten eintragen',
      },
      preview: {
        headline: 'Vorschau eigener Textvorlage',
        loadingText: 'Das Dokument wird geladen...',
      },
      button: {
        dokumentAnsehen: 'Dokument ansehen',
        absenden: 'Text absenden',
        aktivieren: 'Text aktivieren',
      },
    },
  },
};

export const burgschaftBearbeitenWorkflow: AuftraggeberBuergschaftBearbeitenWorkflowSchema = {
  contentHeaderEingegangen: 'Eingegangene Bürgschaft bearbeiten',
  contentHeaderAusgestellt: 'Ausgestellte Bürgschaft bearbeiten',
  hinweisBannerBearbeitet: 'Die Bürgschaft wurde bearbeitet.',
};

export const buergschaftHinzufuegenWorkflow: AuftraggeberBuergschaftHinzufuegenWorkflowSchema = {
  contentHeaderDefault: 'Bürgschaft hinzufügen',
  steps: {
    auswahl: {
      title: 'Auftraggeber- oder Auftragnehmerrolle wählen',
      beschreibung:
        'Wählen Sie aus, ob Sie die Bürgschaft als kreditorische oder als debitorische Bürgschaft zu Ihrem Bestand hinzufügen möchten.',
      radioOptionen: {
        eingegangen: {
          label: 'Eingegangene Bürgschaft hinzufügen',
          subheading:
            'Sie fügen eine kreditorische Bürgschaft als Auftraggeber zur Liste der eingegangenen Bürgschaften hinzu.',
        },
        ausgestellt: {
          label: 'Ausgestellte Bürgschaft hinzufügen',
          subheading:
            'Sie fügen eine debitorische Bürgschaft als Auftragnehmer zur Liste der ausgestellten Bürgschaften hinzu.',
        },
      },
    },
  },
};

export const buergschaftVerwaltenWorkflow: AuftraggeberBuergschaftVerwaltenWorkflowSchema = {
  steps: {
    beteiligte: {
      title: 'Vertragsdaten eingeben',
      beschreibung:
        'Bitte geben Sie die Daten des Bürgen, der die Bürgschaft ausgestellt hat, sowie die der beiden Vertragspartner ein.',

      neuerBuerge: 'Die Anlage von fehlenden Bürgen können Sie hier beantragen.',
      gruppen: {
        buerge: 'Bürge',
        vertragspartner: 'Vertragspartner',
        kreditnehmer: 'Auftragnehmer',
        beguenstigter: 'Auftraggeber',
      },
      fehler: {
        vertragspartner: 'Bitte wählen Sie die Vertragspartner',
        kreditnehmer: 'Bitte wählen Sie einen Auftragnehmer',
        beguenstigter: 'Bitte wählen Sie einen Auftraggeber',
      },
      suche: {
        hinzufuegenLabel: {
          kreditnehmer: 'Daten manuell eingeben',
          beguenstigter: 'Daten manuell eingeben',
        },
        suchLabel: {
          kreditnehmer: 'Auftragnehmer',
          beguenstigter: 'Auftraggeber',
        },
      },
      eingabe: {
        typ: {
          firma: 'Firma',
          oeffentlich: 'Öffentlich',
          privatperson: 'Privatperson',
        },
        suchButton: 'zurück zur Suche',
        name: 'Vorname und Nachname',
        nameLautHandelsregister: 'Name laut Handelsregister',
        gruppe: {
          kreditnehmer: 'Auftragnehmer',
          beguenstigter: 'Auftraggeber',
        },
        manuell: 'Daten manuell eingeben',
      },
      einladung: {
        title: 'Auftragnehmer einladen, digitale Bürgschaften zu beantragen',
        beschreibung:
          'Da der ausgewählte Bürge an Trustlog teilnimmt, hätte diese Bürgschaft digital sein können. Sparen Sie in Zukunft die manuelle Eingabe und nutzen Sie die Vorteile digitaler Bürgschaften, indem Sie den Auftragnehmer davon überzeugen, Bürgschaften für Sie digital ausstellen zu lassen.',
        checkbox:
          'Ja, senden Sie mir bitte eine E-Mail-Vorlage, mit der ich meine Auftragnehmer über die Vorteile digitaler Bürgschaften informieren kann und in der erklärt wird, wie sie diese in Zukunft bei ihren Bürgen beantragen können. (optional)',
      },
    },
    erfassung: {
      title: 'Bürgschaftsdaten erfassen',
      beschreibung:
        'Bitte geben Sie die Bürgschaftsdaten so ein, wie sie auf dem Dokument angegeben sind.',
      gruppen: {
        status: 'Status der Bürgschaft',
        buergschaft: 'Bürgschaft',
        vertrag: 'Bau- oder Werkvertrag',
        laufzeit: 'Laufzeit',
        artDerArbeit: 'Art der Arbeit',
        ortDerArbeit: 'Ort der Arbeit',
        dokument: 'Bürgschaftsdokument',
      },
      felder: {
        buergschaftsnummer: 'Bürgschaftsnummer',
        buergschaftsart: 'Bürgschaftsart',
        buergschaftsbetrag: 'Bürgschaftsbetrag',
        ausstellungsdatum: 'Ausstellungsdatum',
        vertragsdatum: 'Vertragsdatum',
        vertragsnummer: 'Vertragsnummer',
        laufzeit: {
          befristung: {
            unbefristet: 'Unbefristet',
            befristet: 'Befristet',
            ablaufDatum: 'Vorauss. Ablaufdatum (optional)',
            befristungsDatum: 'Befristungsdatum',
          },
        },
        artDerArbeitPlaceholder:
          'Hier können Sie einen Hinweis über die Art der Arbeit hinterlassen …',
        ortDerArbeit: {
          freitext: 'Ort beschreiben',
          adresse: 'Adresse eingeben',
          ortFreitextPlaceholder: 'Bitte beschreiben Sie hier den Ort der Arbeit möglichst genau…',
        },
        dokument: {
          maxErreicht: 'Sie haben bereits die maximale Anzahl von Dokumenten ausgewählt',
          uploading: 'Wird hochgeladen...',
          fehler:
            'Es ist ein Fehler beim Hochladen Ihres Dokuments aufgetreten.\nBitte entfernen Sie diese Datei und versuchen Sie eine in einem anderen Format hochzuladen.',
        },
      },
      hinweise: {
        intro: 'Bitte geben Sie die Daten so ein, wie sie auf der Bürgschaft beschrieben sind.',
        statusFehler: 'Bitte wählen Sie eine Option aus.',
      },
    },
    pruefen: {
      title: 'Eingaben prüfen',
      beschreibung:
        'Bitte überprüfen Sie die von Ihnen angegebenen Daten. Nachdem Sie die Bürgschaft hinzufügt haben, wird diese in Ihrer Bürgschaftsliste angezeigt.',
      aktenvermerk: 'Aktenvermerk',
      felder: {
        internerVermerk: 'Interner Vermerk',
      },
      rows: {
        inanspruchnahme: {
          title: 'Inanspruchnahme',
          subtitles: {
            hoehe: 'Höhe der Inanspruchnahme',
            aktenzeichen: 'Aktenzeichen',
            grund: 'Grund der Inanspruchnahme',
            ansprechpartner: 'Ansprechpartner',
            anschrift: 'Anschrift',
            unterlagen: 'Unterlagen',
          },
        },
        nutzererklaerung: {
          title: 'Nutzererklärung',
          fehler: 'Bitte stimmen Sie der Nutzererklärung zu, um den Prozess fortzuführen',
        },
      },
    },
  },
};

export const avallinieVerwaltenWorkflow: AvallinieVerwaltenWorkflowSchema = {
  banner: {
    erfolg: {
      textHinzufuegen: 'Die Avallinie wurde hinzugefügt.',
      textBearbeiten: 'Die Änderungen wurden gespeichert.',
    },
    warnung: {
      text: 'Die dargestellte Auslastung basiert auf den in Trustlog erfassten Bürgschaften und Anträgen, gegebenenfalls ergänzt durch von Ihnen hinterlegten Stichtagswerte. Für die Qualität der Daten sind Sie selbst verantwortlich.',
    },
  },
  steps: {
    kreditvertrag: {
      title: 'Kreditvertrag auswählen',
      beschreibung:
        'Bitte wählen Sie den oder die Kreditverträge aus, die Sie mit der Avallinie verknüpfen möchten. Ein Kreditvertrag kann jeweils nur mit einer Avallinie verknüpft werden.',
      input: {
        headline: 'Vertragsverknüpfung',
        buerge: {
          label: 'Bürge',
          beantragen: 'Die Anlage von fehlenden Bürgen können Sie hier beantragen.',
        },
        kreditvertrag: {
          label: 'Kreditvertrag',
        },
      },
      loeschen: {
        button: 'Avallinie löschen',
        modal: {
          headline: 'Löschen bestätigen',
          description:
            'Sind Sie sicher, dass Sie diese Avallinie löschen möchten? Verlinkte Kreditverträge, Bürgschaften und Aufträge bleiben bestehen und werden nicht gelöscht. <br>' +
            'Diese Aktion kann nicht rückgängig gemacht werden.',
          button: 'Avallinie löschen',
        },
      },
    },
    rahmen: {
      title: 'Rahmen konfigurieren',
      aktionen: {
        hinzufuegen: 'Avallinie hinzufügen',
      },
      beschreibung:
        'Bitte geben Sie die mit dem Bürgen vereinbarte Linienhöhe ein und legen Sie bei Bedarf den Höchstbetrag je Bürgschaft fest. Optional können Sie noch Sublimits einrichten.',
      rahmenvertragsmodell: {
        headline: 'Rahmenvertragsmodell',
        fehlermeldung: 'Bitte wählen Sie eine Option aus.',
        gesamtrahmen: {
          label: 'Überjähriger Gesamtrahmen',
        },
        jahresrahmen: {
          label: 'Jahresrahmen für Verbandsmitglieder',
        },
      },
      gesamtrahmen: {
        headline: 'Gesamtrahmen',
        gesamtwert: {
          label: 'Linienhöhe',
        },
        einzelwert: {
          label: 'max. Höhe je Bürgschaft (optional)',
        },
      },
      jahresrahmen: {
        headline: 'Jahresrahmen',
        beschreibung:
          'Bitte denken Sie daran, den Jahresrahmen bei Reduzierungen oder Erhöhungen manuell zu aktualisieren.',
        gesamtwert: {
          label: 'Linienhöhe',
        },
        einzelwert: {
          label: 'max. Höhe je Bürgschaft (optional)',
        },
        kalenderjahr: {
          label: 'Kalenderjahr',
        },
      },
      sublimits: {
        headline: 'Sublimits',
        button: {
          hinzufuegen: 'Weiteres Sublimit hinzufügen',
          entfernen: 'Löschen',
        },
        optionen: {
          keine: 'Die Avallinie hat keine Sublimits',
          hat: 'Die Avallinie hat ein oder mehrere Sublimits...',
        },
        sublimit: {
          headline: 'Sublimit',
          buergschaftsarten: 'Bürgschaftsarten',
          optionen: {
            gesamtwert: 'Sublimit in Euro',
            anteil: 'Sublimit in % der Linienhöhe',
          },
          maxGesamtwert: 'Höhe des Sublimits',
          maxAnteil: 'Höhe des Sublimits',
          maxEinzelwert: 'max. Höhe je Bürgschaft (optional)',
        },
      },
    },
    avalzins: {
      title: 'Avalzins konfigurieren',
      beschreibung:
        'Bitte geben Sie optional den mit dem Bürgen vereinbarten Zinssatz ein. Falls die Avallinie Sublimits hat, kann ein unterschiedlicher Zinssatz pro Sublimit eingetragen werden.',
      gesamt: {
        headline: 'Zinssatz Gesamtrahmen',
        input: 'Zinssatz',
      },
      sublimit: {
        headline: 'Zinssatz Sublimit',
        input: 'Zinssatz',
      },
    },
    stichtag: {
      title: 'Auslastung am Stichtag definieren',
      beschreibung:
        'Sie können optional die Auslastung an einem bestimmten Stichtag eintragen. Mithilfe des Stichtags müssen Sie Papierbürgschaften, die vor dem Stichtag ausgestellt wurden, nicht mehr manuell hinzufügen, es sei denn, sie wurden nach dem Stichtag enthaftet oder teilenthaftet.',
      weitereInformationen:
        'Weitere Informationen darüber, wie Sie die Auslastungsdaten aktuell halten, finden Sie { link }.',
      linkText: 'hier',
      button: 'Avallinie hinzufügen',
      gesamt: {
        headline: 'Auslastung Gesamtrahmen',
        input: 'Auslastung',
        date: 'Stichtag',
      },
      sublimit: {
        headline: 'Auslastung Sublimit',
        input: 'Auslastung',
        date: 'Stichtag',
      },
    },
  },
} as const;

export const avallinieHinzufuegenWorkflow: AvallinieHinzufuegenWorkflowSchema = {
  contentHeader: 'Avallinie hinzufügen',
};

export const avallinieBearbeitenWorkflow: AvallinieBearbeitenWorkflowSchema = {
  contentHeader: 'Avallinie bearbeiten',
};

export const inanspruchnahmeWorkflow: AuftraggeberInanspruchnahmeWorkflowSchema = {
  contentHeader: 'Bürgschaft in Anspruch nehmen',
  banner: {
    erfolg: {
      text: 'Die Inanspruchnahme wurde beim Bürgen übermittelt.',
    },
  },
  steps: {
    details: {
      title: 'Inanspruchnahme beschreiben',
      beschreibung:
        'Mit dieser Funktion können Sie Ihre Inanspruchnahme an den Bürgen übermitteln. Für die Inanspruchnahme gelten die mit dem Bürgen vereinbarten Voraussetzungen, für deren Einhaltung allein Sie verantwortlich sind. ',
      footer:
        'Falls nach Ihrem Wissen erforderlich, können Sie hier Unterlagen zur Konkretisierung Ihrer Inanspruchnahme zu dieser Bürgschaft hochladen. Sie können bis zu 10 Dateien (z.B. Anschreiben) mit maximal 10 MB pro Datei hochladen. Mehrere Dokumente können zu einer Datei zusammengefasst werden.',
      footerLegalAddon:
        'Der Bürge behält sich vor, bei Bedarf weitere Unterlagen und Informationen anzufordern.',
      form: {
        grundInanspruchname: {
          label: 'Begründung der Inanspruchnahme',
          placeholder:
            'Bitte legen Sie den Sachverhalt dar, warum die Bürgschaft in Anspruch genommen wird...',
        },
        hoeheInanspruchname: {
          label: 'Höhe der Inanspruchnahme',
        },
        dokumente: {
          label: 'Dokumente',
        },
      },
    },
    ansprechpartner: {
      title: 'Ansprechpartner angeben',
      beschreibung:
        'Geben Sie für mögliche Rückfragen des Bürgen die Kontaktdaten eines Ansprechpartners sowie ggf. das interne Aktenzeichen an. Falls die Anschrift des Ansprechpartners von der Adresse auf der Bürgschaft abweicht, können Sie diese hier auch eintragen.',
      rows: {
        ansprechpartner: {
          title: 'Ansprechpartner',
          meineKontaktdatenEintragen: 'Meine Kontaktdaten eintragen',
        },
        aktenzeichen: {
          title: 'Aktenzeichen',
        },
        postadresse: {
          title: 'Anschrift des Ansprechpartners',
        },
      },
    },
    pruefen: {
      title: 'Eingaben prüfen',
      beschreibung: 'Bitte überprüfen Sie die von Ihnen angegebenen Daten.',
      rows: {
        inanspruchnahme: {
          title: 'Inanspruchnahme',
          subtitles: {
            hoehe: 'Höhe der Inanspruchnahme',
            aktenzeichen: 'Aktenzeichen',
            grund: 'Grund der Inanspruchnahme',
            ansprechpartner: 'Ansprechpartner',
            anschrift: 'Anschrift',
            unterlagen: 'Unterlagen',
          },
        },
        nutzererklaerung: {
          title: 'Nutzererklärung',
          fehler: 'Bitte stimmen Sie der Nutzererklärung zu, um den Prozess fortzuführen',
        },
      },
    },
  },
};

export const teilenthaftungEintragenWorkflow: TeilenthaftungEintragenWorkflowSchema = {
  contentHeader: 'Teilenthaftung eintragen',
  banner: {
    erfolg: {
      text: 'Die Teilenthaftung wurde erfolgreich eingetragen.',
    },
    warnung: {
      header: 'Enthaftung der Bürgschaft',
      text: 'Mit einer Teilenthaftung in Höhe des gesamten, aktuellen Bürgschaftsbetrags gilt die Bürgschaft als enthaftet. Verwenden Sie bitte den Enthaftungsprozess oder ändern Sie ihre Eingabe, falls dies nicht erwünscht ist.',
      textteile: {
        reduzierte: 'reduzierte ',
        weitere: 'weitere ',
        hinweisTextHeader:
          'Die Bürgschaft wurde ursprünglich mit einem Betrag von { urspruenglicherBuergschaftsbetrag} € ausgestellt. Durch eine oder mehrere vorherige Teilenthaftungen wurde dieser Betrag bereits reduziert.',
        weitereHinweisTextHeader:
          'Bisher wurden zu dieser Bürgschaft folgende Teilenthaftungen vorgenommen:',
        hinweisTextBisherigeTeilenthaftungen:
          'Der { zusatzReduziert }Bürgschaftsbetrag wurde von { zwischenBetrag } € um { zusatzWeitere }{ hoechstBetrag } € auf einen Betrag von { reduzierterBetrag } € reduziert.',
      },
    },
  },
  eintragen: {
    title: 'Minderungs- oder reduzierten Betrag eingeben',
    beschreibung:
      'Bitte geben Sie den neuen, reduzierten Bürgschaftsbetrag oder den gewünschten Minderungsbetrag ein, um eine weitere Teilenthaftung vorzunehmen.',
    input: {
      aktuellerBuergschaftsbetrag: 'Aktueller Bürgschaftsbetrag',
      minderungsbetrag: 'Minderungsbetrag',
      hoechstbetrag: 'Reduzierter Bürgschaftsbetrag',
    },
    uebergeordneterFehler: {
      text: 'Bitte passen Sie den Minderungsbetrag oder den reduzierten Bürgschaftsbetrag an.',
      weitereText:
        'Gültige Beträge haben einen Wert von mindestens 0,01 EUR und liegen unter dem aktuellen Bürgschaftsbetrag.',
    },
    button: {
      eintragen: 'Teilenthaftung eintragen',
    },
  },
};

export const vertragDetailSite: VertragDetailSiteSchema = {
  contentHeader: 'Kreditvertrag im Detail',
  banner: {
    erfolg: {
      textZugeordnet: 'Die Bürgschaften wurden dem Kreditvertrag zugeordnet.',
      textZuordnungAufgehoben:
        'Die Vertragszuordnung wurde für die ausgewählten Bürgschaften aufgehoben.',
    },
  },
  details: {
    vertragsnummer: 'Vertragsnummer',
    kreditnehmer: 'KREDITNEHMER',
    internerVerwendungshinweis: 'Interner Verwendungshinweis',
  },
  tabellen: {
    unternehmen: {
      tab: 'Unternehmen',
      searchPlaceholder: 'Suche nach Unternehmen',
    },
    buergschaften: {
      tab: 'Bürgschaften',
      searchPlaceholder: 'Suche nach Auftraggeber oder Bürgschaftsnummer',
      inanspruchnahmeTooltip: 'Unterlagen zur Inanspruchnahme an Bürgen übermittelt.',
      teilenthaftungTooltip: 'Reduzierter Betrag nach Teilenthaftung.',
      emptyState:
        'Dem Kreditvertrag wurden noch keine Bürgschaften zugeordnet oder Sie haben für die zugeordneten Bürgschaften keine Sichtberechtigung',
      button: {
        aufheben: 'Zuordnung aufheben',
        zuordnen: 'Bürgschaften zuordnen',
      },
    },
  },
};

export const vertragDetailZuordnenWorkflow: VertragZuordnenWorkflowSchema = {
  contentHeader: 'Bürgschaften zuordnen',
  button: 'Bürgschaften zuordnen',
  auswahl: {
    title: 'Bürgschaften auswählen',
    error: {
      nichtAusgewaehlt: 'Bitte wählen Sie eine Option aus',
      keineNichtZugeordnetenBuergschaften:
        'Alle ausgestellten Bürgschaften der { buergeName } wurden bereits einem Kreditvertrag zugeordnet',
      keineBuergschaften: 'Keine ausgestellten Bürgschaften der { buergeName } gefunden',
    },
    beschreibung:
      'Bitte wählen Sie die Bürgschaften aus, die im Rahmen des Kreditvertrags ausgestellt wurden.',
    radioOptionen: {
      alle: {
        label: 'Alle ausgestellten Bürgschaften der { buergeName } zuordnen',
        subheading:
          'Alle ausgestellten Bürgschaften der { buergeName }, die noch keinem Vertrag zugeordnet sind, werden diesem Kreditvertrag zugeordnet.',
      },
      einzeln: {
        label: 'Nur ausgewählte Bürgschaften zuordnen...',
        subheading:
          'Wählen Sie aus den ausgestellten, noch nicht zugeordneten Bürgschaften der { buergeName } diejenigen aus, die dem Kreditvertrag zugeordnet werden sollen.',
      },
    },
  },
  table: {
    emptyState:
      'Es sieht so aus, als wären keine Bürgschaften auf der Plattform hinterlegt oder alle bereits Verträgen zugeordnet.',
    error: 'Bitte wählen Sie mindestens eine Bürgschaft aus',
    placeholder: 'Suche nach Bürgschaftsnummer',
  },
};

export const vertragAnlegenWorkflow: VertragAnlegenWorkflowSchema = {
  contentHeader: 'Kreditvertrag hinzufügen',
  eingeben: {
    title: 'Vertragsdaten eingeben',
    beschreibung: 'Bitte geben Sie die Vertragsdaten ein.',
    input: {
      headline: 'Vertragsdaten',
      buerge: {
        label: 'Bürge',
        beantragen: 'Die Anlage eines fehlenden Bürgen beantragen',
      },
      kreditnehmer: {
        label: 'Kreditnehmer',
      },
      vertragsnummer: {
        label: 'Vertragsnummer',
      },
    },
  },
  verknuepfen: {
    title: 'Unternehmen verknüpfen',
    beschreibung:
      'Bitte wählen Sie die Unternehmen aus, die diesen Vertrag für die Beauftragung und Ausstellung von Bürgschaften verwenden dürfen.',
    suchePlaceholder: 'Suche nach Unternehmen',
    table: {
      header: {
        unternehmen: 'Unternehmen',
        anschrift: 'Anschrift',
        status: 'Status',
      },
      mutterunternehmen: 'Mutterunternehmen',
      kreditnehmer: 'Kreditnehmer',
    },
  },
  zusammenfassung: {
    title: 'Eingaben prüfen',
    beschreibung: {
      vertragsbuerge:
        'Bitte überprüfen Sie die von Ihnen angegebenen Daten. Nachdem Sie den Vertrag hinzufügt haben, wird dieser zur Prüfung an Ihren Bürgen gesendet.',
      fremdbuerge:
        'Bitte überprüfen Sie die von Ihnen angegebenen Daten. Da es sich bei diesem Vertrag um einen nicht an Trustlog teilnehmenden Bürgen handelt, können Sie diese Daten jederzeit bearbeiten.',
    },
    button: 'Vertrag hinzufügen',
    groups: {
      headline: 'Vertragsdaten',
      buerge: 'Bürge',
      kreditnehmer: 'Kreditnehmer',
      vertragsnummer: 'Vertragsnummer',
      verknupefteUnternehmen: 'Verknüpfte Unternehmen',
    },
    internerVerwendungshinweis: {
      headline: 'Interner Verwendungshinweis',
      placeholder:
        'Beschreiben Sie Besonderheiten, die bei der Verwendung des Vertrages zu beachten sind...',
    },
    irreversibleText:
      'Bitte beachten Sie, dass das Hinzufügen eines Vertrages mit einem an Trustlog teilnehmenden Bürgen nicht rückgängig gemacht werden kann.',
    hinweisBanner: {
      vertragsbuerge: 'Der Kreditvertrag wurde hinzugefügt und liegt dem Bürgen zur Prüfung vor.',
      fremdbuerge: 'Der Kreditvertrag wurde hinzugefügt.',
    },
  },
};

export const vertragBearbeitenWorkflowSchema: VertragBearbeitenWorkflowSchema = {
  hinweisbanner: {
    nichtLoeschbarMitZugeordneterAvallinie:
      'Kreditverträge können nur gelöscht werden, wenn sie keiner Avallinie zugeordnet sind.',
    avallinieOeffnen: 'Avallinie öffnen',
  },
};
